import React, { useEffect, useState } from "react";
import {
  FormControl,
  TextField,
  Box,
  CircularProgress,
  Avatar,
  IconButton,
  FormHelperText,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { useForm, useController } from "react-hook-form";
import clsx from "clsx";
import FlatList from "flatlist-react/lib";
import {
  getRecruiterProfileList,
  setSelectedRecruiter,
} from "../../../actions/recruiter";
import OAuth from "../../OAuth";
import { Verified, Close } from "@mui/icons-material";
import { RotatingLines } from "react-loader-spinner";
import { yupResolver } from "@hookform/resolvers/yup";
import { object, string } from "yup";

const PER_PAGE = "10";

const validationSchema = object().shape({
  selectedRecruiterProfile: string().required("Recruiter is required"),
});

const RecruiterSelector = () => {
  const [page, setPage] = useState(1);

  const [searchQuery, setSearchQuery] = useState("");
  const [showList, setShowList] = useState(false);

  const dispatch = useDispatch();
  const {
    control,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: "all",
  });

  const recruiterState = useSelector((state) => state.recruiterReducer);
  const recruiterProfileList = useSelector(
    (state) => state.recruiterReducer.recruiterProfileList || []
  );
  const { recruiterProfileListMetaData, loading } = recruiterState;

  const selectedRecruiter = useSelector(
    (state) => state.recruiterReducer.selectedRecruiter
  );

  const { field } = useController({
    control,
    name: "selectedRecruiterProfile",
    defaultValue: selectedRecruiter || null,
  });

  const fetchDataFromApi = async (page, perPage, searchQuery) => {
    dispatch(
      getRecruiterProfileList(
        { page, perPage, searchedKeyword: searchQuery },
        () => {},
        (err) => console.log("Error: ", err)
      )
    );
  };

  useEffect(() => {
    setPage(1);
    fetchDataFromApi(1, PER_PAGE, searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    handleClearSearch();
  }, []);

  const onReachEnd = async () => {
    if (
      recruiterProfileListMetaData?.hasMore &&
      !recruiterProfileListMetaData?.loading
    ) {
      const newPage = page + 1;
      setPage(newPage);
      await fetchDataFromApi(newPage, PER_PAGE, searchQuery);
    }
  };

  const handleSearchChange = (event) => {
    if (!field.value) {
      setSearchQuery(event.target.value);
      setShowList(true);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    dispatch(setSelectedRecruiter(null));
    setValue("selectedRecruiterProfile", null);
    setShowList(false);
  };

  const onSelectRecruiter = (recruiter) => {
    setValue("selectedRecruiterProfile", recruiter);
    dispatch(setSelectedRecruiter(recruiter));
    setSearchQuery("");
    setShowList(false);
  };

  const renderRecruiterItem = (recruiter) => (
    <Box
      key={recruiter.recruiterId}
      className={clsx(
        "border-[1px] border-[#d7d7d7] rounded-[4px] p-2 m-1 flex items-center gap-2 cursor-pointer"
      )}
      onClick={() => onSelectRecruiter(recruiter)}
    >
      <OAuth
        altSrc={recruiter?.userProfile?.profilePic?.url}
        component={
          <Avatar
            sx={{ width: 50, height: 50 }}
            src={recruiter?.userProfile?.profilePic?.url}
          />
        }
      />
      <div className="flex flex-col">
        <div className="flex items-center gap-1">
          <span className="font-['Poppins-Semibold']">
            {recruiter?.firstName + " " + recruiter?.lastName ?? "-"}
          </span>
          {recruiter?.verified && (
            <Verified className="text-[#5D43FF] text-base" />
          )}
        </div>
        <span className="text-md text-gray-500">{recruiter?.email ?? "-"}</span>
        <span className="text-xs text-gray-500">
          {recruiter?.userProfile?.profileCategory?.name ?? "-"}
        </span>
      </div>
    </Box>
  );

  const renderLoader = () => {
    if (recruiterProfileListMetaData?.loading) {
      return (
        <div className="flex justify-center w-full py-2">
          <RotatingLines
            strokeColor="#000"
            strokeWidth="3"
            animationDuration="0.75"
            width="40"
            visible={true}
          />
        </div>
      );
    } else {
      return <></>;
    }
  };

  const ListEmpty = () => {
    return (
      <div className="text-center text-[16px] font-['Poppins-Medium'] p-4">
        No Recruiter Found
      </div>
    );
  };

  return (
    <FormControl className="w-full p-4" variant="standard">
      <TextField
        label="Search Recruiter"
        value={searchQuery}
        onChange={handleSearchChange}
        onFocus={() => setShowList(true)}
        InputProps={{
          startAdornment: selectedRecruiter && (
            <Box className="flex items-center gap-2">
              <Avatar
                sx={{ width: 35, height: 35, marginRight: 1 }}
                src={selectedRecruiter?.userProfile?.profilePic?.url}
              />
              <span className="font-['Poppins-Semibold']">
                {selectedRecruiter?.firstName ?? "-"}
              </span>
            </Box>
          ),
          endAdornment: (
            <>
              {loading && <CircularProgress color="inherit" size={20} />}
              {(showList || field.value) && (
                <IconButton onClick={handleClearSearch} size="small">
                  <Close />
                </IconButton>
              )}
            </>
          ),
        }}
      />

      {showList && (
        <Box className="max-h-[300px] overflow-auto mt-2 border rounded-md">
          <FlatList
            list={[...recruiterProfileList]}
            renderItem={renderRecruiterItem}
            hasMoreItems={recruiterProfileListMetaData?.hasMore}
            loadMoreItems={onReachEnd}
            paginationLoadingIndicator={renderLoader()}
            renderWhenEmpty={ListEmpty}
          />
        </Box>
      )}
      {errors?.selectedRecruiterProfile?.message && (
        <FormHelperText
          className="ml-0 font-['Poppins-Regular']"
          id="component-error-text"
        >
          {errors?.selectedRecruiterProfile?.message}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default RecruiterSelector;
